import { startTransition, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useSiteRestDays } from '@hooks/useSiteRestDays';
import { readJsonPref, writeJsonPref } from '@utils/localJsonPref';
import { projectToday } from '../activitiesFilter';
import { daysBetween } from '../../SchedulerPage/lib/timeScale';
import { mondayOf, PLAN_WEEKS, windowFor, type IPlanWindow, type PlanWeeks } from '../WorkPlan/workPlanModel';

const KEY_PREFIX = 'dorca.activities.window.';

/** First visit: two weeks — this week's review plus the one being prepared. */
export const DEFAULT_WINDOW_WEEKS: PlanWeeks = 2;

/** The window length last chosen on this site (validated; the default else). */
export function readWindowWeeks(siteId: string): PlanWeeks {
  const stored = readJsonPref(KEY_PREFIX + siteId);
  return PLAN_WEEKS.find((weeks) => weeks === stored) ?? DEFAULT_WINDOW_WEEKS;
}

export interface IDateWindow {
  /** Null until the site's zone resolves — "today" is the site's today. */
  window: IPlanWindow | null;
  today: string;
  restDays: ReadonlySet<number>;
  weeks: PlanWeeks;
  setWeeks: (weeks: PlanWeeks) => void;
  /** Page by whole weeks. */
  page: (weeks: number) => void;
  /** Show the week holding `day` (`YYYY-MM-DD`). */
  jumpTo: (day: string) => void;
}

/**
 * The page's ONE date picker: whole weeks from the Monday of the site's
 * current week (1, 2 or 4 of them), paged by week. Every view reads it — the
 * Daily List and the Progress Tracker show what overlaps it, the Work Plan
 * draws it. The length is remembered per site; the paging is not, so the page
 * reopens on this week. Changes land at transition priority, so a relayout
 * never blocks the click.
 */
export function useDateWindow(siteId: string, weeklyDaysOff: number[] | undefined): IDateWindow {
  const tz = useSelector(selectSiteTz);
  const today = tz ? projectToday(tz) : '';
  const restDays = useSiteRestDays(siteId, weeklyDaysOff);
  const [weeks, setWeeksState] = useState(() => readWindowWeeks(siteId));
  const [offset, setOffset] = useState(0);
  const window = useMemo(() => (today ? windowFor(today, weeks, offset, restDays) : null), [today, weeks, offset, restDays]);

  const setWeeks = useCallback(
    (next: PlanWeeks) => {
      writeJsonPref(KEY_PREFIX + siteId, next);
      startTransition(() => setWeeksState(next));
    },
    [siteId],
  );
  const page = useCallback((by: number) => startTransition(() => setOffset((prev) => prev + by)), []);
  const jumpTo = useCallback(
    (day: string) => startTransition(() => setOffset(daysBetween(mondayOf(today), mondayOf(day)) / 7)),
    [today],
  );

  return { window, today, restDays, weeks, setWeeks, page, jumpTo };
}
